import { normalizeHistorySearchMode } from "../../../features/search/utils/historySearchMetadata";

export const getLocalYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildUtcRangeForLocalDateRange = (startDateStr, endDateStr) => {
  const orderedStart = startDateStr <= endDateStr ? startDateStr : endDateStr;
  const orderedEnd = startDateStr <= endDateStr ? endDateStr : startDateStr;
  const [startYear, startMonth, startDay] = orderedStart.split("-").map(Number);
  const [endYear, endMonth, endDay] = orderedEnd.split("-").map(Number);
  const localStart = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
  const localEnd = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
  return {
    start: localStart.toISOString(),
    end: localEnd.toISOString(),
    startDate: orderedStart,
    endDate: orderedEnd,
  };
};

export const buildUtcRangeForDateSelection = (filterStartDate, filterEndDate) => {
  const todayDate = getLocalYYYYMMDD(new Date());
  const startDateValue = filterStartDate || todayDate;
  const endDateValue = filterEndDate || startDateValue;
  return buildUtcRangeForLocalDateRange(startDateValue, endDateValue);
};

export const resolveDownloadFilename = (headers, fallback) => {
  const contentDisposition = headers.get("content-disposition") || "";
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

export const downloadBlob = (blob, filename, contentType) => {
  const url = window.URL.createObjectURL(
    contentType ? new Blob([blob], { type: contentType }) : blob,
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const formatTimestamp = (timestamp, language) => {
  try {
    const ts = timestamp.endsWith("Z") || timestamp.includes("+") ? timestamp : `${timestamp}Z`;
    return new Date(ts).toLocaleString(
      language === "ja" ? "ja-JP" : "en-US",
    );
  } catch {
    return timestamp;
  }
};

export const formatDuration = (seconds, t) => {
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  return `${seconds.toFixed(2)}${t.seconds}`;
};

export const formatCost = (cost) => {
  if (!cost) return "$0.00";
  return `$${parseFloat(cost).toFixed(4)}`;
};

export const formatPreference = (value) => {
  if (!value) return "N/A";
  return String(value).replace(/_/g, " ");
};

export const formatSearchMode = (value, t) => {
  const normalized = normalizeHistorySearchMode(value);
  if (normalized === "basic") return t.basicMode;
  if (normalized === "pro") return t.proMode;
  return "N/A";
};

export const formatCredits = (value, language) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 1) return "N/A";
  return language === "ja"
    ? `${count}クレジット`
    : `${count} ${count === 1 ? "credit" : "credits"}`;
};

export const resolveLogRowId = (log, index = "") =>
  log.query_id || `${log.user_id || "unknown"}-${log.session_id || index}`;

export const resolvePaperAnalysisKey = (log) =>
  `${log.user_id || ""}\u0000${log.session_id || ""}`;

export const resolveSearchEngagementKey = (log) =>
  `${log.user_id || ""}\u0000${log.session_id || ""}`;

export const resolvePaperAnalysisEngagementKey = (log) =>
  `${log.user_id || ""}\u0000${log.session_id || ""}`;

export const hasSearchEngagementEvents = (engagement) => {
  if (!engagement) return false;
  return Boolean(
    engagement.papers_seen ||
      engagement.title_clicks ||
      engagement.full_text_clicks ||
      engagement.bookmark_adds ||
      engagement.bookmark_removes ||
      engagement.csv_exports ||
      engagement.history_opens,
  );
};

export const hasPaperAnalysisEngagementEvents = (engagement) => {
  if (!engagement) return false;
  return Boolean(
    engagement.copies ||
      engagement.word_exports ||
      engagement.feedback_up ||
      engagement.feedback_down,
  );
};

export const normalizePaperPmid = (paper) =>
  String(paper?.pmid || paper?.PMID || "").trim();
